import React, { useEffect, useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { cn } from "../../lib/utils";

export const isExternal = (url = "") => /^https?:\/\//i.test(url);

/** CTA anchor used across hero/blocks. `onCta` receives (label, url, event). */
export const CtaLink = ({ label, url, onCta, className, variant = "solid", size = "lg" }) => {
  if (!label) return null;
  const href = url || "#";
  const external = isExternal(href);
  const base = "inline-flex items-center justify-center gap-2 rounded-none font-semibold uppercase tracking-[0.2em] transition-colors duration-300";
  const sizes = size === "lg" ? "h-12 px-7 text-[11px]" : "h-10 px-5 text-[10px]";
  const variants =
    variant === "solid"
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "border border-foreground/30 bg-transparent text-foreground hover:border-foreground hover:bg-foreground hover:text-background";
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer noopener" : undefined}
      onClick={(e) => onCta && onCta(label, href, e)}
      className={cn(base, sizes, variants, className)}
      data-testid="lp-cta"
    >
      {label} <ArrowUpRight className="h-4 w-4" />
    </a>
  );
};

const MS = { day: 86400000, hour: 3600000, minute: 60000, second: 1000 };

const splitDuration = (ms) => ({
  days: Math.floor(ms / MS.day),
  hours: Math.floor((ms % MS.day) / MS.hour),
  minutes: Math.floor((ms % MS.hour) / MS.minute),
  seconds: Math.floor((ms % MS.minute) / MS.second),
});

const CountdownCell = ({ value, label }) => (
  <div className="min-w-[64px] border border-border bg-background/60 px-3 py-2 text-center">
    <p className="display text-3xl leading-none">{String(value).padStart(2, "0")}</p>
    <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
  </div>
);

/** Live countdown to an ISO timestamp. Renders nothing for invalid dates. */
export const Countdown = ({ expiresAt }) => {
  const [left, setLeft] = useState(null);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const end = new Date(expiresAt).getTime();
    const tick = () => setLeft(Math.max(0, end - Date.now()));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (left === null || Number.isNaN(left)) return null;
  if (left === 0) return <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Offer ended</p>;

  const { days, hours, minutes, seconds } = splitDuration(left);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lp-countdown">
      <Clock className="mr-1 h-4 w-4 text-primary" />
      <CountdownCell value={days} label="days" />
      <CountdownCell value={hours} label="hrs" />
      <CountdownCell value={minutes} label="min" />
      <CountdownCell value={seconds} label="sec" />
    </div>
  );
};

export const SectionHeading = ({ children, className }) =>
  children ? <h2 className={cn("display text-[clamp(2.5rem,6vw,5.5rem)] uppercase leading-[0.88] tracking-wide", className)}>{children}</h2> : null;

export const Body = ({ children, className }) =>
  children ? <p className={cn("whitespace-pre-line text-base leading-relaxed text-muted-foreground md:text-lg", className)}>{children}</p> : null;
